
import Vue, { RenderContext } from "vue";
import _ from "@/boot/lodash";
import router from "@/router";
import store from "@/store";
import { Contexts } from "@/models/contexts";
import { DataModules } from "@/store/modules/data";
import { UpmindObjectTypes as UOT } from "@/data/enums/objects";
import i18n from "@/i18n";
import { ClientRoutes, AdminRoutes } from "@/data/enums/router";

function resolveRoute(objectBrandId, route) {
  const brandId = _.get(store.state, "auth.admin.brandId");
  if (objectBrandId === brandId) return route;
  const resolved = router.resolve(route);
  return {
    name: "adminBrand",
    params: {
      id: objectBrandId,
      redirect: resolved.href
    }
  };
}
export default Vue.extend({
  functional: true,
  render(h, { props, data, children }: RenderContext) {
    const { object, objectType, objectBrandId } = props;
    if (_.isNull(object)) return h("div", data, children);
    const context = store.state.context;
    const isAdmin = context === Contexts.ADMIN;
    switch (objectType) {
      case "invoice":
        // If a basket
        if (!_.get(object, "number")) {
          if (!isAdmin) {
            return h("div", data, children);
          }
          return h(
            "router-link",
            _.merge(data, {
              props: {
                to: resolveRoute(objectBrandId, {
                  name: "adminOrderBasketView",
                  params: {
                    basketId: _.get(object, "id")
                  }
                })
              }
            }),
            children
          );
        }
        // If an invoice
        if (isAdmin) {
          // Admin
          return h(
            "a",
            _.merge(data, {
              on: {
                click: () => {
                  store.dispatch(
                    `data/${DataModules.INVOICES}/openPreviewModal`,
                    {
                      invoiceId: _.get(object, "id")
                    }
                  );
                }
              }
            }),
            children
          );
        } else {
          // Client
          return h(
            "router-link",
            _.merge(data, {
              props: {
                to: {
                  name: ClientRoutes.INVOICE,
                  params: {
                    invoiceId: _.get(object, "id")
                  }
                }
              }
            }),
            children
          );
        }
      case "invoice_payment":
        return h(
          "a",
          _.merge(data, {
            on: {
              click: () => {
                store.dispatch(
                  `data/${DataModules.INVOICES}/openPreviewModal`,
                  {
                    invoiceId: _.get(object, "invoice_id")
                  }
                );
              }
            }
          }),
          children
        );
      case "contract":
        if (!object?.main_invoice_id) return h("a", data, children);
        if (isAdmin) {
          return h(
            "a",
            _.merge(data, {
              on: {
                click: () => {
                  store.dispatch(
                    `data/${DataModules.ORDERS}/openPreviewModal`,
                    {
                      props: {
                        orderId: _.get(object, "main_invoice_id")
                      }
                    }
                  );
                }
              }
            }),
            children
          );
        } else {
          return h(
            "router-link",
            _.merge(data, {
              props: {
                to: {
                  name: ClientRoutes.ORDER,
                  params: {
                    orderId: _.get(object, "main_invoice_id")
                  }
                }
              }
            }),
            children
          );
        }
      case "contracts_product":
        return h(
          "a",
          _.merge(data, {
            on: {
              click: () => {
                store.dispatch(
                  `data/${DataModules.CONTRACTS_PRODUCTS}/openQuickLookModal`,
                  {
                    contractId: _.get(object, "contract_id"),
                    productId: object?.id,
                    productAttributeId: object?.id,
                    productOptionId: object?.id,
                    cProdIdentifier: _.get(object, "product_name")
                  }
                );
              }
            }
          }),
          children
        );
      case "ticket":
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: isAdmin
                  ? AdminRoutes.SUPPORT_TICKET
                  : ClientRoutes.SUPPORT_TICKET,
                params: {
                  ticketId: _.get(object, "id")
                }
              })
            }
          }),
          children
        );
      case "ticket_user":
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: isAdmin
                  ? AdminRoutes.SUPPORT_TICKET
                  : ClientRoutes.SUPPORT_TICKET,
                params: {
                  ticketId: _.get(object, "ticket_id")
                }
              })
            }
          }),
          children
        );
      case "ticket_message":
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: isAdmin
                  ? AdminRoutes.SUPPORT_TICKET
                  : ClientRoutes.SUPPORT_TICKET,
                params: {
                  ticketId: _.get(object, "ticket_id")
                }
              })
            }
          }),
          children
        );
      case "client":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          object ? "router-link" : "p",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: "adminClient",
                params: {
                  clientId: _.get(object, "id")
                }
              })
            }
          }),
          children
        );
      case "clients_email":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          object ? "router-link" : "p",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: "adminClientProfile",
                params: {
                  clientId: _.get(object, "client_id")
                }
              })
            }
          }),
          children
        );
      case "clients_payment_detail":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          object ? "router-link" : "p",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: "adminClientProfile",
                params: {
                  clientId: _.get(object, "client_id")
                }
              })
            }
          }),
          children
        );
      case "user":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: "adminUserOverview",
                params: {
                  userId: _.get(object, "id")
                }
              })
            }
          }),
          children
        );
      case "account":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: "adminUserOverview",
                params: {
                  userId: _.get(object, "user_id")
                }
              })
            }
          }),
          children
        );
      case "bulk_action":
        if (!isAdmin) return h("div", data, children);
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: _.get(
                  {
                    [UOT.CLIENT]: "adminClientBulkActionsEdit",
                    [UOT.CONTRACTS_PRODUCT]: "adminProductBulkActionsEdit"
                  },
                  object.object_type,
                  "adminClientBulkActionsEdit"
                ),
                params: {
                  bulkActionId: _.get(object, "id")
                }
              })
            }
          }),
          children
        );
      case "template":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: AdminRoutes.EMAIL_TEMPLATE_EDIT,
                params: {
                  templateId: _.get(object, "id")
                }
              })
            }
          }),
          children
        );
      case "credit_debit_transaction":
        return h(
          "a",
          _.merge(data, {
            on: {
              click: () => {
                store.dispatch(`data/${DataModules.LOGS}/openLogModal`, {
                  props: {
                    log: object,
                    title: i18n.t("_.transaction"),
                    deep: 1
                  }
                });
              }
            }
          }),
          children
        );
      case "product":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: "adminCatalogueProduct",
                params: {
                  productId: _.get(object, "id")
                }
              })
            }
          }),
          children
        );
      case "brand":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          "router-link",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: "brandGeneralSettings",
                params: {
                  brandId: _.get(object, "id")
                }
              })
            }
          }),
          children
        );
      case "promotion":
        if (!isAdmin) {
          return h("div", data, children);
        }
        return h(
          object ? "router-link" : "p",
          _.merge(data, {
            props: {
              to: resolveRoute(objectBrandId, {
                name: "admin__promotion",
                params: {
                  promotionId: _.get(object, "id")
                }
              })
            }
          }),
          children
        );
      default:
        return h("div", data, children);
    }
  }
});
